import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '20px'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
    },
    title: {
        fontSize: '28px',
        fontWeight: '700',
        color: 'var(--text-primary)',
        letterSpacing: '-0.5px'
    },
    tabs: {
        display: 'flex',
        gap: '2px',
        background: 'var(--border-color)',
        padding: '4px',
        borderRadius: '12px',
        marginBottom: '24px',
        width: 'fit-content'
    },
    tab: {
        padding: '10px 20px',
        borderRadius: '8px',
        border: 'none',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s',
        color: 'var(--text-muted)',
        background: 'transparent'
    },
    activeTab: {
        background: 'var(--bg-primary)',
        color: 'var(--text-primary)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    summaryBar: {
        display: 'flex',
        gap: '16px',
        marginBottom: '24px',
        flexWrap: 'wrap'
    },
    summaryChip: {
        background: 'var(--bg-primary)',
        border: '1px solid var(--border-color)',
        borderRadius: '12px',
        padding: '12px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontSize: '14px',
        color: 'var(--text-secondary)',
        fontWeight: '500'
    },
    alertBar: {
        background: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        color: '#991b1b',
        fontSize: '14px',
        fontWeight: '500'
    },
    box: {
        background: 'var(--bg-primary)',
        padding: '24px',
        borderRadius: '16px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
        border: '1px solid var(--border-color)'
    },
    searchInput: {
        width: '100%',
        maxWidth: '400px',
        padding: '12px 16px',
        fontSize: '14px',
        border: '1px solid var(--border-color)',
        borderRadius: '12px',
        background: 'var(--bg-secondary)',
        outline: 'none',
        color: 'var(--text-primary)'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse'
    },
    th: {
        background: 'var(--bg-secondary)',
        padding: '16px 20px',
        textAlign: 'left',
        borderBottom: '1px solid var(--border-color)',
        color: 'var(--text-muted)',
        fontSize: '12px',
        fontWeight: '600',
        textTransform: 'uppercase'
    },
    td: {
        padding: '16px 20px',
        borderBottom: '1px solid var(--border-color)',
        color: 'var(--text-secondary)',
        fontSize: '14px'
    },
    productImage: {
        width: '40px',
        height: '40px',
        borderRadius: '8px',
        objectFit: 'cover',
        border: '1px solid var(--border-color)',
        background: 'var(--bg-secondary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        color: 'var(--text-muted)'
    },
    criticalStock: {
        color: '#dc2626',
        fontWeight: '600',
        background: '#fef2f2',
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '12px'
    },
    outOfStock: {
        color: '#991b1b',
        fontWeight: '700',
        background: '#fee2e2',
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '12px'
    }
};

export default function StoreInventory() {
    const { userData, currentUser } = useAuth();
    const [activeTab, setActiveTab] = useState('store'); // 'store' or 'main'
    const [storeProducts, setStoreProducts] = useState([]);
    const [mainProducts, setMainProducts] = useState([]);
    const [canViewMainStock, setCanViewMainStock] = useState(false);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (!userData || userData.role !== 'store' || !userData.companyId) return;

        const companyId = userData.companyId;
        const storeId = currentUser.uid;

        const fetchData = async () => {
            // Check permissions
            const storeDocRef = doc(db, 'companies', companyId, 'stores', storeId);
            const storeDocSnap = await getDoc(storeDocRef);
            if (storeDocSnap.exists()) {
                const storeData = storeDocSnap.data();
                if (storeData.permissions?.canViewMainStock) {
                    setCanViewMainStock(true);
                }
            }

            // Sync Store Inventory
            const unsubStoreInv = onSnapshot(collection(db, 'companies', companyId, 'stores', storeId, 'inventory'), (snap) => {
                setStoreProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
                setLoading(false);
            });

            // Sync Main Inventory
            const unsubMainInv = onSnapshot(collection(db, 'companies', companyId, 'products'), (snap) => {
                setMainProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            });

            return { unsubStoreInv, unsubMainInv };
        };

        let unsubs = { unsubStoreInv: () => {}, unsubMainInv: () => {} };
        fetchData().then(res => unsubs = res);

        return () => {
            if (unsubs.unsubStoreInv) unsubs.unsubStoreInv();
            if (unsubs.unsubMainInv) unsubs.unsubMainInv();
        };
    }, [userData, currentUser]);

    const displayedProducts = useMemo(() => {
        const source = activeTab === 'main' ? mainProducts : storeProducts;
        if (!searchTerm) return source;
        return source.filter(p => 
            (p.name && p.name.toLowerCase().includes(searchTerm.toLowerCase())) || 
            (p.barcode && p.barcode.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    }, [activeTab, mainProducts, storeProducts, searchTerm]);

    const summaryStats = useMemo(() => {
        const source = activeTab === 'main' ? mainProducts : storeProducts;
        const totalTypes = source.length;
        const totalQty = source.reduce((s, p) => s + (p.quantity || 0), 0);
        const criticalCount = source.filter(p => (p.quantity || 0) <= (p.criticalLevel || 0) && (p.criticalLevel || 0) > 0).length;
        const outOfStockCount = source.filter(p => (p.quantity || 0) === 0).length;
        return { totalTypes, totalQty, criticalCount, outOfStockCount };
    }, [activeTab, mainProducts, storeProducts]);

    if (loading) return <div style={styles.container}><p style={{color: 'var(--text-secondary)'}}>Yükleniyor...</p></div>;

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Stoklar</h1>
            </div>

            {canViewMainStock && (
                <div style={styles.tabs}>
                    <button 
                        style={{ ...styles.tab, ...(activeTab === 'store' ? styles.activeTab : {}) }}
                        onClick={() => setActiveTab('store')}
                    >
                        Mağaza Stokları
                    </button>
                    <button 
                        style={{ ...styles.tab, ...(activeTab === 'main' ? styles.activeTab : {}) }}
                        onClick={() => setActiveTab('main')}
                    >
                        Merkez Stokları (Görüntüleme)
                    </button>
                </div>
            )}

            {/* Summary bar */}
            <div style={styles.summaryBar}>
                <div style={styles.summaryChip}>
                    <span>📦</span>
                    <span><strong>{summaryStats.totalTypes}</strong> ürün çeşidi</span>
                </div>
                <div style={styles.summaryChip}>
                    <span>🔢</span>
                    <span><strong>{summaryStats.totalQty}</strong> toplam adet</span>
                </div>
                {summaryStats.outOfStockCount > 0 && (
                    <div style={{ ...styles.summaryChip, borderColor: '#fecaca', background: '#fef2f2' }}>
                        <span>🚫</span>
                        <span style={{ color: '#991b1b' }}><strong>{summaryStats.outOfStockCount}</strong> tükenen ürün</span>
                    </div>
                )}
            </div>

            {/* Critical stock alert */}
            {summaryStats.criticalCount > 0 && activeTab === 'store' && (
                <div style={styles.alertBar}>
                    <span style={{ fontSize: '20px' }}>⚠️</span>
                    <span><strong>{summaryStats.criticalCount}</strong> ürün kritik stok seviyesinin altında! Merkeze stok talebi göndermeyi düşünün.</span>
                </div>
            )}

            <div style={styles.box}>
                <div style={{ marginBottom: '24px' }}>
                    <input 
                        style={styles.searchInput} 
                        type="text" 
                        placeholder="Ürün adı veya barkod ara..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div style={{ overflowX: 'auto' }}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Görsel</th>
                                <th style={styles.th}>Ürün Adı</th>
                                <th style={styles.th}>Barkod</th>
                                <th style={styles.th}>Stok Miktarı</th>
                                <th style={styles.th}>Satış Fiyatı</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayedProducts.map(p => {
                                const qty = p.quantity || 0;
                                const criticalLevel = p.criticalLevel || 0;
                                const isOutOfStock = qty === 0;
                                const isCritical = !isOutOfStock && criticalLevel > 0 && qty <= criticalLevel;

                                return (
                                    <tr key={p.id}>
                                        <td style={styles.td}>
                                            {p.imageUrl ? (
                                                <img src={p.imageUrl} alt={p.name} style={styles.productImage} />
                                            ) : (
                                                <div style={styles.productImage}>📦</div>
                                            )}
                                        </td>
                                        <td style={{ ...styles.td, fontWeight: '500', color: 'var(--text-primary)' }}>{p.name}</td>
                                        <td style={styles.td}>{p.barcode || '-'}</td>
                                        <td style={styles.td}>
                                            {isOutOfStock ? (
                                                <span style={styles.outOfStock}>Tükendi</span>
                                            ) : isCritical ? (
                                                <span style={styles.criticalStock}>
                                                    ⚠ {qty} {p.unit || 'Adet'}
                                                </span>
                                            ) : (
                                                <span style={{ fontWeight: '600' }}>
                                                    {qty} {p.unit || 'Adet'}
                                                </span>
                                            )}
                                        </td>
                                        <td style={styles.td}>₺{(p.salePrice || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                                    </tr>
                                );
                            })}
                            {displayedProducts.length === 0 && (
                                <tr>
                                    <td colSpan="5" style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>
                                        Stok kaydı bulunamadı.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
